import { Box, Typography, Chip, IconButton, Collapse, Autocomplete, TextField } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { Move } from "../../types/move";
import { MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_STATUS } from "../../move/move";
import { PokemonV2 } from "../../pokemon/pokemon";
import { useMemo, useState, KeyboardEventHandler } from "react";
import { getSortedMovesForAllSelect } from "../../lib/move-rules";
import { normalizeForSearch } from "../../utils/search";

interface InputMovesProps {
  selectedPokemon: PokemonV2;
  selectedMoves: Move[];
  availableMoves: Move[];
  onMoveSelect: (moveIndex: number, move: Move | null) => void;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;
}

function InputMoves({ 
  selectedPokemon, 
  selectedMoves, 
  availableMoves, 
  onMoveSelect,
  getMoveDisplayName,
  getMoveDisplayPower
}: InputMovesProps) {
  // 4枠固定の技スロットを配列化して扱う
  const moveSlots = Array.from({ length: 4 }, (_, index) => selectedMoves[index] || null);
  const [isMovesExpanded, setIsMovesExpanded] = useState(false); // デフォルトで閉じた状態

  // 使用頻度の高い技のIDを取得（攻撃技と変化技の両方を含む）
  const oftenUsedMoveIds = useMemo(() => {
    const topMoveIds = selectedPokemon.often_used_move.slice(0, 10);
    return new Set(
      topMoveIds.filter(moveId => availableMoves.some(move => move.id === moveId))
    );
  }, [availableMoves, selectedPokemon.often_used_move]);

  const handleToggleMoves = () => {
    setIsMovesExpanded(prev => !prev);
  };

  const handleToggleMovesByKey: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleMoves();
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* ヘッダー部分（能力設定と同じスタイル） */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 2,
          cursor: 'pointer',
          userSelect: 'none'
        }}
        role="button"
        tabIndex={0}
        onClick={handleToggleMoves}
        onKeyDown={handleToggleMovesByKey}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            技選択
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            {selectedMoves.length}/4
          </Typography>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleToggleMoves();
            }}
            sx={{ color: 'text.secondary' }}
          >
            {isMovesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isMovesExpanded}>
          {moveSlots.map((move, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={getSortedMovesForAllSelect(
                    selectedPokemon,
                    availableMoves.filter(availableMove =>
                      // 現在のスロットに設定されている技、または他のスロットで選択されていない技のみ表示
                      !selectedMoves.some((selectedMove, selectedIndex) =>
                        selectedMove && selectedMove.id === availableMove.id && selectedIndex !== index
                      )
                    )
                  ).sortedMoves}
                  filterOptions={(options, { inputValue }) => {
                    // ひらがな/カタカナの揺れを吸収して検索できるようにする（例: 「あ」で「アイアンヘッド」もヒット）
                    if (!inputValue) return options;
                    const normalizedInput = normalizeForSearch(inputValue);
                    const filtered = options.filter((option) =>
                      normalizeForSearch(getMoveDisplayName(option, selectedPokemon.id)).includes(normalizedInput)
                    );
                    return getSortedMovesForAllSelect(selectedPokemon, filtered).sortedMoves;
                  }}
                  getOptionLabel={(option) => {
                    const displayName = getMoveDisplayName(option, selectedPokemon.id);
                    const displayPower = getMoveDisplayPower(option, selectedPokemon.id);
                    if (option.damage_class_number === MOVE_DAMAGE_CLASS_STATUS) {
                      return `${displayName} (変化技)`;
                    } else {
                      const damageClass = option.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ? '物理' : '特殊';
                      return `${displayName} (威力: ${displayPower}, ${damageClass})`;
                    }
                  }}
                  value={move}
                  onChange={(event, newValue) => {
                    onMoveSelect(index, newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`技${index + 1}`}
                      placeholder="技名を入力して検索..."
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const isOftenUsed = oftenUsedMoveIds.has(option.id);
                    return (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          fontWeight: isOftenUsed ? 'bold' : 'normal',
                          backgroundColor: isOftenUsed ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            backgroundColor: isOftenUsed ? '#bbdefb' : '#f5f5f5'
                          },
                          '&[aria-selected="true"]': {
                            backgroundColor: isOftenUsed ? '#bbdefb' : '#e0e0e0'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {isOftenUsed && (
                            <Chip
                              label="TOP10"
                              size="small"
                              color="primary"
                              sx={{ 
                                marginRight: 1, 
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          )}
                          <Typography variant="body2">
                            {option.damage_class_number === MOVE_DAMAGE_CLASS_STATUS 
                              ? `${getMoveDisplayName(option, selectedPokemon.id)} (変化技)`
                              : `${getMoveDisplayName(option, selectedPokemon.id)} (威力: ${getMoveDisplayPower(option, selectedPokemon.id)})`
                            }
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                />
              </Box>
            </Box>
          ))}
      </Collapse>
    </Box>
  );
}

export default InputMoves;
